import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRightSquare, ShoppingBag, Truck, Sparkles, Zap, Package,
  Store, Users, Bot, GraduationCap, Wallet, TrendingUp, ChevronRight,
  CheckCircle2, Award, Target, Globe, ShieldCheck, Timer, Star, Share2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import CinematicBackground from "@/components/hero/CinematicBackground";

/* ------------------------------------------------------------------ */
/* Small primitives                                                   */
/* ------------------------------------------------------------------ */
function Pill({ children, testid }) {
  return (
    <span data-testid={testid} className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold bg-[#FBBF24]/10 border border-[#FBBF24]/40 text-[#FBBF24]">
      {children}
    </span>
  );
}

function SectionEyebrow({ children, testid }) {
  return (
    <div data-testid={testid} className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold mb-3">
      <Sparkles size={12} /> {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* PUBLIC HEADER                                                      */
/* ------------------------------------------------------------------ */
function PublicHeader({ user }) {
  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0F2042]/85 border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="landing-logo">
          <span className="font-display text-[#FBBF24] tracking-[0.18em] text-[10px] md:text-xs">ZENITH FLOW</span>
          <span className="font-display font-black tracking-tight text-sm md:text-base">DYNAMICS</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-6 text-xs uppercase tracking-[0.18em] font-semibold text-white/70">
          <Link to="/how-it-works" className="hover:text-[#FBBF24]" data-testid="nav-link-how">How It Works</Link>
          <Link to="/membership" className="hover:text-[#FBBF24]" data-testid="nav-link-membership">Membership</Link>
          <Link to="/partner-rewards" className="hover:text-[#FBBF24]" data-testid="nav-link-rewards">Partner Rewards</Link>
          <Link to="/success-stories" className="hover:text-[#FBBF24]" data-testid="nav-link-stories">Success Stories</Link>
          <Link to="/about" className="hover:text-[#FBBF24]" data-testid="nav-link-about">About</Link>
          <Link to="/faq" className="hover:text-[#FBBF24]" data-testid="nav-link-faq">FAQ</Link>
          <Link to="/contact" className="hover:text-[#FBBF24]" data-testid="nav-link-contact">Contact</Link>
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          {user ? (
            <Link to="/dashboard" className="text-xs uppercase tracking-widest font-bold bg-[#FBBF24] text-[#0F2042] px-3 py-2 rounded-md hover:bg-yellow-300" data-testid="landing-open-dashboard">
              Open Dashboard
            </Link>
          ) : (
            <>
              <Link to="/login" className="text-xs uppercase tracking-widest font-bold text-white/80 px-3 py-2 rounded-md hover:text-[#FBBF24]" data-testid="landing-login">Login</Link>
              <Link to="/register" className="text-xs uppercase tracking-widest font-bold bg-[#FBBF24] text-[#0F2042] px-4 py-2 rounded-md hover:bg-yellow-300" data-testid="landing-join">Join Now</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* HERO                                                                */
/* ------------------------------------------------------------------ */
function Hero() {
  const { user } = useAuth();
  return (
    <section className="relative overflow-hidden pt-20 pb-24 md:pt-28 md:pb-32" data-testid="hero-section">
      {/* 3D cinematic scene (z-0) + gradient scrim (z-10). */}
      <CinematicBackground />
      <div
        className="absolute inset-0 z-[5] bg-no-repeat bg-cover bg-right opacity-[0.06] md:opacity-[0.08] pointer-events-none mix-blend-overlay"
        style={{ backgroundImage: "url('/landing-backdrop.png')" }}
        aria-hidden="true"
      />

      <div className="relative z-20 max-w-7xl mx-auto px-5 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Pill testid="hero-eyebrow"><Sparkles size={12} /> Commerce-as-a-Service Platform</Pill>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]" data-testid="hero-headline">
              Start Your Online Business.
              <span className="block text-[#FBBF24]">No Inventory.</span>
              <span className="block">No Warehouse.</span>
              <span className="block text-white/85">No Experience Required.</span>
            </h1>
            <p className="mt-6 text-white/70 text-base md:text-lg max-w-2xl leading-relaxed" data-testid="hero-subheadline">
              Build your own online business using our products, technology, and fulfillment network.
              Sell anywhere, earn retail profits, grow your customer base, and build a long-term business —
              while we handle sourcing, warehousing, and shipping.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="hero-ctas">
              <Link
                to={user ? "/dashboard" : "/register"}
                className="btn-deploy inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold uppercase tracking-widest"
                data-testid="hero-cta-primary"
              >
                <ArrowUpRightSquare size={16} /> {user ? "Open Dashboard" : "Become a Partner"}
              </Link>
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold uppercase tracking-widest border border-white/20 text-white/85 hover:border-[#FBBF24] hover:text-[#FBBF24]"
                data-testid="hero-cta-secondary"
              >
                <ShoppingBag size={16} /> Browse Products
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Right-side quick stats card */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="glass-card gold-border p-6 md:p-7"
            data-testid="hero-summary-card"
          >
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 font-bold mb-1">Your Partner Toolkit</div>
            <div className="font-display text-2xl font-black text-white">Everything to start selling — day one.</div>
            <div className="mt-5 grid grid-cols-2 gap-4">
              {[
                { icon: Store, label: "Personal Online Store" },
                { icon: Package, label: "Real Product Catalog" },
                { icon: Truck, label: "Nationwide Fulfillment" },
                { icon: Bot, label: "AI Marketing Assistant" },
              ].map((it) => (
                <div key={it.label} className="flex items-start gap-2">
                  <it.icon size={16} className="text-[#FBBF24] mt-0.5 shrink-0" />
                  <div className="text-sm text-white/85 font-medium leading-tight">{it.label}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-5 border-t border-white/10 grid grid-cols-3 gap-3">
              <StatLine label="Retail Margin" value="Up to 25%" />
              <StatLine label="Referral Bonus" value="10%" />
              <StatLine label="Team Levels" value="5 Deep" accent="blue" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Trust bar */}
      <div className="relative z-20 max-w-7xl mx-auto px-5 mt-14">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[11px] uppercase tracking-[0.2em] text-white/55 font-semibold" data-testid="trust-bar">
          {[
            { icon: Globe, text: "Philippine-Based Operations" },
            { icon: Truck, text: "Nationwide Delivery" },
            { icon: Package, text: "Real Products" },
            { icon: ShieldCheck, text: "Secure Payments" },
            { icon: Bot, text: "AI-Powered Commerce" },
            { icon: Award, text: "Wholesale Pricing" },
            { icon: Store, text: "Personal Online Store" },
          ].map((t) => (
            <div key={t.text} className="inline-flex items-center gap-1.5">
              <t.icon size={13} className="text-[#FBBF24]" /> {t.text}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatLine({ label, value, accent = "gold" }) {
  const map = { gold: "text-[#FBBF24]", white: "text-white", blue: "text-[#93C5FD]" };
  return (
    <div className="flex flex-col">
      <div className="text-[9px] uppercase tracking-[0.22em] text-white/40 font-semibold">{label}</div>
      <div className={`font-mono-num text-lg font-bold ${map[accent]} mt-0.5`}>{value}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* WHY CHOOSE ZENITH                                                   */
/* ------------------------------------------------------------------ */
function WhyChoose() {
  const cards = [
    { icon: Package, title: "Real Products", body: "Physical goods across electronics, health, beauty, fashion, food, and lifestyle categories — sourced, warehoused, and shipped by us." },
    { icon: Store, title: "No Inventory Needed", body: "Your personal online store lists our catalog. You never touch stock. When a customer orders, we ship." },
    { icon: Truck, title: "Dropshipping Fulfillment", body: "Nationwide delivery handled end-to-end. Track every order from checkout to doorstep in real time." },
    { icon: Share2, title: "Partner Rewards", body: "Earn retail margin on every sale, plus referral bonuses and team overrides — commissions funded by real product transactions." },
    { icon: Bot, title: "AI Business Assistant", body: "Generate product captions, video scripts, and descriptions on demand. Ship marketing content in seconds, not hours." },
    { icon: Zap, title: "Fast Withdrawals", body: "Convert commission credits to pesos and cash out on your schedule. No lock-ups, no waiting cycles." },
    { icon: GraduationCap, title: "Marketing Academy", body: "Ready-to-use assets and step-by-step training — from your first post to your first thousand customers." },
    { icon: Users, title: "24/7 Support", body: "Message the support inbox any time. Real humans backed by our AI assistant so no question goes unanswered." },
  ];
  return (
    <section className="relative py-20 md:py-24 border-t border-white/5" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto px-5">
        <SectionEyebrow testid="why-choose-eyebrow">Why Choose Zenith</SectionEyebrow>
        <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight max-w-3xl">
          A complete online business —{" "}
          <span className="text-[#FBBF24]">without the overhead.</span>
        </h2>
        <p className="text-white/60 max-w-2xl mt-3">
          Zenith Flow Dynamics gives entrepreneurs everything modern commerce needs: real products, fulfillment,
          AI tools, and a rewards system that actually pays out from real sales.
        </p>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              viewport={{ once: true, amount: 0.4 }}
              className="glass-card p-5"
              data-testid={`why-card-${i}`}
            >
              <c.icon size={22} className="text-[#FBBF24]" />
              <div className="mt-3 font-display font-black">{c.title}</div>
              <div className="mt-1 text-sm text-white/65 leading-relaxed">{c.body}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// (removed defunct Share2Icon wrapper — Share2 imported from lucide-react)

/* ------------------------------------------------------------------ */
/* HOW IT WORKS                                                        */
/* ------------------------------------------------------------------ */
function HowItWorks() {
  const steps = [
    {
      n: "01", title: "Become a Partner",
      body: "Choose a Business Starter Kit, activate your account, and receive your personal online store, dashboard, and toolkit.",
      icon: Award,
    },
    {
      n: "02", title: "Share & Sell",
      body: "Promote products using your affiliate link. Use ready-made marketing materials, AI-generated captions, and video scripts. Take customer orders from anywhere.",
      icon: Share2,
    },
    {
      n: "03", title: "We Fulfill",
      body: "Zenith ships directly to your customers. You never touch inventory. Every order is tracked and updated in your dashboard.",
      icon: Truck,
    },
    {
      n: "04", title: "Earn",
      body: "Retail profit + referral commissions + leadership rewards land in your Commission Wallet. Withdraw to your bank anytime.",
      icon: Wallet,
    },
  ];
  return (
    <section className="relative py-20 md:py-24 border-t border-white/5" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-5">
        <SectionEyebrow testid="how-it-works-eyebrow">How It Works</SectionEyebrow>
        <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">
          Four steps.{" "}
          <span className="text-[#FBBF24]">Zero guesswork.</span>
        </h2>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              viewport={{ once: true, amount: 0.4 }}
              className="glass-card gold-border p-6 relative"
              data-testid={`how-step-${i + 1}`}
            >
              <div className="text-[#FBBF24] font-mono-num text-4xl font-black opacity-40">{s.n}</div>
              <s.icon size={22} className="text-[#FBBF24] absolute top-6 right-6" />
              <div className="mt-2 font-display text-xl font-black">{s.title}</div>
              <div className="mt-2 text-sm text-white/65 leading-relaxed">{s.body}</div>
            </motion.div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/how-it-works" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-[#FBBF24] hover:text-yellow-300" data-testid="how-it-works-cta">
            Full breakdown <ChevronRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* MEMBERSHIP TIERS                                                    */
/* ------------------------------------------------------------------ */
function MembershipTiers() {
  const tiers = [
    {
      name: "Starter Partner",
      priceLabel: "Entry Kit",
      price: "₱999",
      retail: "Up to 10% retail margin",
      accent: "border-white/15",
      features: [
        "2 physical products (retail value ₱1,598)",
        "Personal online store",
        "Affiliate dashboard & links",
        "AI marketing assistant",
        "Digital onboarding & training",
        "Marketing materials library",
      ],
      cta: "Start Selling",
    },
    {
      name: "Business Builder",
      priceLabel: "Growth Kit",
      price: "₱2,499",
      retail: "Up to 18% retail margin",
      accent: "border-[#FBBF24]/60 gold-border",
      badge: "Most Popular",
      features: [
        "5 physical products",
        "Higher wholesale discount",
        "Faster fulfillment tier",
        "Bonus marketing asset pack",
        "Team override eligibility (L1-L3)",
        "Priority AI content generation",
      ],
      cta: "Build My Business",
    },
    {
      name: "Executive Partner",
      priceLabel: "Executive Kit",
      price: "₱4,999",
      retail: "Up to 25% retail margin",
      accent: "border-[#FBBF24]",
      features: [
        "12 physical products",
        "Deepest wholesale pricing",
        "Priority support",
        "Full team override tree (L1-L5)",
        "Monthly leadership incentives",
        "1:1 mentor pairing",
      ],
      cta: "Go Executive",
    },
  ];
  return (
    <section className="relative py-20 md:py-24 border-t border-white/5" data-testid="membership-section">
      <div className="max-w-7xl mx-auto px-5">
        <SectionEyebrow testid="membership-eyebrow">Membership</SectionEyebrow>
        <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">
          Pick your Business Starter Kit.
        </h2>
        <p className="text-white/60 max-w-2xl mt-3">
          Every kit ships with real products, your personal online store, AI tools, and full training —
          so you can start selling the day you activate.
        </p>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {tiers.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              viewport={{ once: true, amount: 0.3 }}
              className={`glass-card p-6 border ${t.accent} relative`}
              data-testid={`tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {t.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest font-bold bg-[#FBBF24] text-[#0F2042] px-3 py-1 rounded-full">
                  {t.badge}
                </span>
              )}
              <div className="text-[11px] uppercase tracking-[0.22em] text-white/50 font-bold">{t.priceLabel}</div>
              <div className="font-display text-2xl font-black mt-1">{t.name}</div>
              <div className="font-mono-num text-3xl font-black text-[#FBBF24] mt-3">{t.price}</div>
              <div className="text-[#FBBF24] text-sm font-semibold mt-1">{t.retail}</div>
              <ul className="mt-5 space-y-2 text-sm text-white/75">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="text-[#FBBF24] mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/register"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-bold uppercase tracking-widest bg-[#FBBF24] text-[#0F2042] hover:bg-yellow-300"
                data-testid={`tier-cta-${i}`}
              >
                {t.cta} <ArrowUpRightSquare size={14} />
              </Link>
            </motion.div>
          ))}
        </div>
        <p className="text-center text-[11px] uppercase tracking-[0.22em] text-white/40 mt-8">
          All memberships include AI tools · Ongoing training · No lock-in
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* PARTNER REWARDS                                                     */
/* ------------------------------------------------------------------ */
function PartnerRewardsBlock() {
  const items = [
    { icon: TrendingUp, title: "Retail Profit", body: "Buy at partner wholesale price. Sell at retail. Keep the margin on every unit — from 10% to 25% depending on your tier." },
    { icon: Users, title: "Direct Referral Bonus", body: "Earn a flat percentage (illustrative: 10%) of the qualifying Starter Kit purchase whenever you enroll a new partner." },
    { icon: Award, title: "Team Sales Overrides", body: "Illustrative: 5% L1 · 3% L2 · 2% L3 · 1% L4 · 1% L5. All overrides are funded by real product transactions in your team." },
    { icon: Target, title: "Monthly Incentives", body: "Hit performance targets to unlock leadership bonuses, community recognition, and priority payout perks." },
  ];
  return (
    <section className="relative py-20 md:py-24 border-t border-white/5" data-testid="rewards-section">
      <div className="max-w-7xl mx-auto px-5">
        <SectionEyebrow testid="rewards-eyebrow">Partner Rewards Program</SectionEyebrow>
        <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight max-w-3xl">
          Earn by helping others build{" "}
          <span className="text-[#FBBF24]">their business.</span>
        </h2>
        <p className="text-white/60 max-w-2xl mt-3">
          Every commission stream is funded by real product sales, not membership fees.
          Qualification rules keep incentives aligned with genuine retail commerce.
        </p>

        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {items.map((r, i) => (
            <motion.div
              key={r.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              viewport={{ once: true, amount: 0.4 }}
              className="glass-card p-5"
              data-testid={`reward-card-${i}`}
            >
              <r.icon size={22} className="text-[#FBBF24]" />
              <div className="mt-3 font-display font-black">{r.title}</div>
              <div className="mt-1 text-sm text-white/65 leading-relaxed">{r.body}</div>
            </motion.div>
          ))}
        </div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-white/40 mt-8 text-center">
          Earnings vary with sales performance and are not guaranteed
        </p>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* TESTIMONIALS                                                        */
/* ------------------------------------------------------------------ */
function Testimonials() {
  const items = [
    {
      name: "Marisol V.",
      role: "Beauty Store Partner · Cebu",
      quote: "I made my first sale two days after activating my online store. No inventory, no shipping headaches — I just posted and shared. My kids now help me pack orders on weekends.",
    },
    {
      name: "Rico A.",
      role: "Electronics Partner · Metro Manila",
      quote: "The AI captions saved me hours a week. I focus on customer chats and reels; Zenith handles the rest. My best week so far paid better than my old office salary.",
    },
    {
      name: "Jenna P.",
      role: "Home & Living Partner · Davao",
      quote: "What I love most: my customers are real people, not points on a dashboard. Zenith made me a real business owner — with real products in real homes.",
    },
  ];
  return (
    <section className="relative py-20 md:py-24 border-t border-white/5" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-5">
        <SectionEyebrow testid="testimonials-eyebrow">Success Stories</SectionEyebrow>
        <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight max-w-3xl">
          Real partners.{" "}
          <span className="text-[#FBBF24]">Real customers.</span>{" "}
          Real growth.
        </h2>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {items.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              viewport={{ once: true, amount: 0.4 }}
              className="glass-card p-6"
              data-testid={`story-${i}`}
            >
              <div className="flex items-center gap-1 text-[#FBBF24] mb-3">
                {[0, 1, 2, 3, 4].map((s) => (<Star key={s} size={14} fill="#FBBF24" />))}
              </div>
              <p className="text-sm text-white/80 leading-relaxed italic">&ldquo;{t.quote}&rdquo;</p>
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="font-display font-black">{t.name}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/45 font-semibold">{t.role}</div>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/success-stories" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-[#FBBF24] hover:text-yellow-300" data-testid="stories-cta">
            Read more stories <ChevronRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* FINAL CTA                                                           */
/* ------------------------------------------------------------------ */
function FinalCTA() {
  const { user } = useAuth();
  return (
    <section className="relative py-24 border-t border-white/5" data-testid="final-cta-section">
      <div className="max-w-4xl mx-auto px-5 text-center">
        <SectionEyebrow testid="final-cta-eyebrow">Ready to start?</SectionEyebrow>
        <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight">
          Build. Sell. Earn. <span className="text-[#FBBF24]">Grow.</span>
        </h2>
        <p className="text-white/65 max-w-2xl mx-auto mt-4">
          Join thousands of partners running modern online businesses on Zenith Flow Dynamics.
          Your store, your customers, your income — powered by our fulfillment network.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to={user ? "/dashboard" : "/register"}
            className="btn-deploy inline-flex items-center gap-2 px-8 py-3 rounded-md text-sm font-bold uppercase tracking-widest"
            data-testid="final-cta-primary"
          >
            <ArrowUpRightSquare size={16} /> {user ? "Open Dashboard" : "Become a Partner"}
          </Link>
          <Link
            to="/simulator"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-md text-sm font-bold uppercase tracking-widest border border-white/20 text-white/85 hover:border-[#FBBF24] hover:text-[#FBBF24]"
            data-testid="final-cta-simulator"
          >
            <Timer size={16} /> Try Income Simulator
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* FOOTER                                                              */
/* ------------------------------------------------------------------ */
function Footer() {
  return (
    <footer className="border-t border-white/10 pt-14 pb-10 bg-[#0A162F]" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-5 grid gap-10 md:grid-cols-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#FBBF24] font-bold">Zenith Flow</div>
          <div className="font-display text-2xl font-black tracking-tight">DYNAMICS</div>
          <p className="text-sm text-white/55 mt-3 max-w-xs">
            The future of social commerce. Build a modern online business with real products,
            AI tools, and a transparent partner rewards program.
          </p>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/45 font-bold mb-3">Company</div>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/about" className="hover:text-[#FBBF24]" data-testid="footer-about">About</Link></li>
            <li><Link to="/how-it-works" className="hover:text-[#FBBF24]" data-testid="footer-how">How It Works</Link></li>
            <li><Link to="/success-stories" className="hover:text-[#FBBF24]" data-testid="footer-stories">Success Stories</Link></li>
            <li><Link to="/contact" className="hover:text-[#FBBF24]" data-testid="footer-contact">Contact</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/45 font-bold mb-3">Partner</div>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/membership" className="hover:text-[#FBBF24]" data-testid="footer-membership">Business Starter Kits</Link></li>
            <li><Link to="/partner-rewards" className="hover:text-[#FBBF24]" data-testid="footer-rewards">Partner Rewards</Link></li>
            <li><Link to="/simulator" className="hover:text-[#FBBF24]" data-testid="footer-simulator">Income Simulator</Link></li>
            <li><Link to="/register" className="hover:text-[#FBBF24]" data-testid="footer-join">Become a Partner</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/45 font-bold mb-3">Legal</div>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/faq" className="hover:text-[#FBBF24]" data-testid="footer-faq">FAQ</Link></li>
            <li><Link to="/contact" className="hover:text-[#FBBF24]">Privacy Policy</Link></li>
            <li><Link to="/contact" className="hover:text-[#FBBF24]">Terms of Service</Link></li>
            <li><Link to="/contact" className="hover:text-[#FBBF24]">Refund Policy</Link></li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-5 mt-10 pt-6 border-t border-white/5 text-[11px] uppercase tracking-[0.2em] text-white/40 text-center">
        © {new Date().getFullYear()} Zenith Flow Dynamics · The Future of Social Commerce
      </div>
    </footer>
  );
}

/* ------------------------------------------------------------------ */
/* PAGE                                                                */
/* ------------------------------------------------------------------ */
export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#0F2042] text-white" data-testid="landing-page">
      <PublicHeader user={user} />
      <Hero />
      <WhyChoose />
      <HowItWorks />
      <MembershipTiers />
      <PartnerRewardsBlock />
      <Testimonials />
      <FinalCTA />
      <Footer />
    </div>
  );
}
